import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import StoricoVerificheIntegrazione, { VerificaIntegrazione } from '../../components/StoricoVerificheIntegrazione';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './IntegrazioneZohoSignPage.api';

interface Credenziale {
  nome: string;
  etichetta: string;
  /** null = non leggibile (credenziali GCP o permessi), non necessariamente mancante */
  presente: boolean | null;
  errore?: string;
}

interface StatoZohoSign {
  datacenter: string;
  credenziali: Credenziale[];
  verifiche: VerificaIntegrazione[];
  richieste: { giorni: number; perStato: { stato: string; numero: number }[] };
}

interface EsitoVerifica {
  esitoOk: boolean;
  messaggio: string;
  durataMs: number;
}

const STATO_RICHIESTA_LABEL: Record<string, string> = {
  INVIATA: 'In attesa di firma',
  FIRMATA: 'Firmate',
  RIFIUTATA: 'Rifiutate',
  RECALL: 'Annullate',
  SCADUTA: 'Scadute',
};

/**
 * Zoho Sign: firma digitale dei preventivi. Le credenziali sono di gruppo
 * (Secret Manager) e valgono per tutte le società: qui si vede se ci sono,
 * se funzionano e quanto vengono usate.
 */
export default function IntegrazioneZohoSignPage() {
  const { societaId } = useFiltroSocieta();
  const { can } = usePermesso();
  const puoVerificare = can('impostazioni.integrazione_zoho_sign', 'edit');

  const [stato, setStato] = useState<StatoZohoSign | null>(null);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState('');
  const [verificaInCorso, setVerificaInCorso] = useState(false);
  const [esito, setEsito] = useState<EsitoVerifica | null>(null);

  const carica = useCallback(async () => {
    try {
      setCaricamento(true);
      setErrore('');
      const qs = societaId ? `?societaId=${societaId}` : '';
      // @api GET /api/impostazioni/zoho-sign/stato?societaId=N -> { success: true, data: { datacenter, credenziali: [{ nome, etichetta, presente }], verifiche: VerificaIntegrazione[], richieste: { giorni, perStato: [{ stato, numero }] } } }
      const resp = await apiFetch<{ success: boolean; data: StatoZohoSign }>(`/api/impostazioni/zoho-sign/stato${qs}`);
      setStato(resp.data);
    } catch (err: any) {
      setErrore(err?.message || 'Errore nel caricamento dello stato di Zoho Sign');
    } finally {
      setCaricamento(false);
    }
  }, [societaId]);

  useEffect(() => { carica(); }, [carica]);

  const verifica = async () => {
    try {
      setVerificaInCorso(true);
      setEsito(null);
      // @api POST /api/impostazioni/zoho-sign/verifica -> { success: true, data: { esitoOk, messaggio, durataMs } }
      const resp = await apiFetch<{ success: boolean; data: EsitoVerifica }>('/api/impostazioni/zoho-sign/verifica', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setEsito(resp.data);
      carica();
    } catch (err: any) {
      setEsito({ esitoOk: false, messaggio: err?.message || 'Errore nella verifica', durataMs: 0 });
    } finally {
      setVerificaInCorso(false);
    }
  };

  const totaleRichieste = stato?.richieste.perStato.reduce((s, r) => s + r.numero, 0) ?? 0;

  return (
    <Layout>
      <div className="bop-page-header">
        <div>
          <h1 className="bop-page-title">Zoho Sign</h1>
          <p className="bop-text-muted">
            Firma digitale dei preventivi. Le credenziali sono uniche per tutto il gruppo e si gestiscono in Secret Manager.
          </p>
        </div>
      </div>

      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

      {caricamento && !stato ? (
        <div className="bop-card">
          <div className="bop-empty-state"><p>Caricamento...</p></div>
        </div>
      ) : stato && (
        <>
          <div className="bop-card">
            <div className="bop-card__header">
              <h3 className="bop-card__title">Credenziali di gruppo</h3>
              {puoVerificare && (
                <button type="button" className="bop-btn bop-btn-secondary" onClick={verifica} disabled={verificaInCorso}>
                  {verificaInCorso ? 'Verifica in corso...' : 'Verifica ora'}
                </button>
              )}
            </div>
            <div className="bop-card__body">
              <p className="bop-integrazione__nota">Datacenter: {stato.datacenter}</p>
              <ul className="bop-integrazione__lista">
                {stato.credenziali.map(c => (
                  <li key={c.nome} className="bop-integrazione__voce">
                    <span className={`bop-integrazione__dot bop-integrazione__dot--${c.presente ? 'ok' : c.presente === null ? 'attenzione' : 'errore'}`} />
                    <span className="bop-integrazione__etichetta">{c.etichetta}</span>
                    <code className="bop-integrazione__codice">{c.nome}</code>
                    <span
                      className={`bop-badge ${c.presente ? 'bop-badge--success' : c.presente === null ? 'bop-badge--warning' : 'bop-badge--danger'}`}
                      title={c.errore}
                    >
                      {c.presente ? 'Presente' : c.presente === null ? 'Non leggibile' : 'Mancante'}
                    </span>
                  </li>
                ))}
              </ul>
              {esito && (
                <div className={`bop-alert ${esito.esitoOk ? 'bop-alert--success' : 'bop-alert--danger'} bop-mt-3`}>
                  {esito.esitoOk ? 'Credenziali valide: ' : 'Verifica fallita: '}{esito.messaggio}
                  {esito.durataMs > 0 && ` (${esito.durataMs} ms)`}
                </div>
              )}
            </div>
          </div>

          <div className="bop-card">
            <div className="bop-card__header">
              <h3 className="bop-card__title">Richieste di firma negli ultimi {stato.richieste.giorni} giorni</h3>
            </div>
            <div className="bop-card__body">
              {totaleRichieste === 0 ? (
                <p className="bop-integrazione__nota">Nessuna richiesta di firma digitale inviata nel periodo.</p>
              ) : (
                <ul className="bop-integrazione__lista">
                  {stato.richieste.perStato.map(r => (
                    <li key={r.stato} className="bop-integrazione__voce">
                      <span className="bop-integrazione__etichetta">{STATO_RICHIESTA_LABEL[r.stato] || r.stato}</span>
                      <span className="bop-integrazione__numero">{r.numero}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <StoricoVerificheIntegrazione verifiche={stato.verifiche} />
        </>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
